import React from 'react';
import { useNavigate } from 'react-router-dom';

import { LookoBulletList, LookoButton, LookoModalV2, VStack } from '@looko-design-system';
import { Typography } from '@mui/material';
import { createQueryString } from '@toss/utils';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { useShopInfo2 } from '@/app/shop/shop-info/hooks';
import { 원스톱_솔루션_라우터 } from '@/router/one-stop-solution/routes';

import { useH_코너로지스_결제_상태_조회_쿼리 } from '../services/useH-코너로지스-결제-상태-조회-쿼리';

const STORAGE_KEY = 'one-stop-solution-payment-modal-last-seen-date';
const persistAtom = atomWithStorage(STORAGE_KEY, dayjs().subtract(1, 'day').aimsDate);

/**
 * 원스톱 솔루션 비용을 입금하지 않은 경우, 매일 최초 1회 경고 모달 노출.
 */
export const C원스톱_솔루션_비용_입금_안내_모달 = () => {
  const [lastSeenDate, setLastSeenDate] = useAtom(persistAtom);

  const navigate = useNavigate();

  const { shopInfo } = useShopInfo2();

  const 원스톱_솔루션_사용여부 = !!shopInfo.cornerLogis?.useCornerLogis || !!shopInfo.exitCornerLogis;

  const { data } = useH_코너로지스_결제_상태_조회_쿼리({
    shopID: shopInfo.shopID,
    options: {
      enabled: 원스톱_솔루션_사용여부,
    },
  });

  const 모달_노출하는가 = React.useMemo(() => {
    if (!data?.hasUnpaidSettlement) {
      return false;
    }

    return !lastSeenDate || dayjs(lastSeenDate).isBefore(dayjs(), 'day');
  }, [data?.hasUnpaidSettlement, lastSeenDate]);

  const onClose = () => {
    setLastSeenDate(dayjs().aimsDate);
  };

  const handleNavigate = () => {
    navigate(
      `${원스톱_솔루션_라우터.월별정산.path}${createQueryString({
        month: dayjs(data?.month).format('MM'),
        year: dayjs(data?.month).format('YYYY'),
        pageNumber: 1,
        pageSize: 10,
      })}`,
    );
    onClose();
  };

  if (!원스톱_솔루션_사용여부) {
    return null;
  }

  return (
    <LookoModalV2 open={모달_노출하는가}>
      <LookoModalV2.Header>원스톱 솔루션 비용 입금 안내</LookoModalV2.Header>

      <LookoModalV2.Content>
        <VStack gap={3} width={600}>
          <Typography variant="Body_Looko">
            <b>{dayjs(data?.month).format('YYYY년 M월')}</b> 원스톱 솔루션 이용금액은{' '}
            {(data?.totalPrice ?? 0).aimsPriceWon} (VAT 포함) 이며, 납부 기한은 <b>이번달 5일</b>까지입니다. 입금이
            확인되지 않는 경우, 솔루션 접속이 제한될 수 있습니다.
          </Typography>

          <Typography variant="Body_Looko">
            <LookoBulletList
              items={[
                '입금을 하신 경우에도, 아직 담당자 확인 전에는 본 팝업이  노출될 수 있습니다. 이 경우, 다음 영업일에  담당자가 확인 후에, 팝업이 사라질 예정이오니 참고부탁드립니다.',
                '관련하여 문의사항이 있으신 경우, 담당 영업팀으로 연락주세요.',
              ]}
            />
          </Typography>
        </VStack>
      </LookoModalV2.Content>

      <LookoModalV2.Actions>
        <LookoButton onClick={handleNavigate}>
          <b>입금정보 확인하러 가기</b>
        </LookoButton>
        <LookoButton variant="outlined" onClick={onClose}>
          닫기
        </LookoButton>
      </LookoModalV2.Actions>
    </LookoModalV2>
  );
};
